import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, ArrowLeft, Lock, Save, Calculator, Target, TrendingUp, Info, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PROV = {
  OWNER_PROVIDED: { label: "You entered this", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  CALCULATED: { label: "Calculated", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ASSUMPTION: { label: "Assumption", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  MODELED: { label: "Modeled (what-if)", cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

function Badge({ status }) {
  const p = PROV[status] || PROV.CALCULATED;
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${p.cls}`}>{p.label}</span>;
}

const FIELDS = [
  { k: "revenue", label: "Sales / revenue", hint: "Total money in for the period" },
  { k: "cogs", label: "Cost of goods sold (COGS)", hint: "Direct cost of what you sell" },
  { k: "labor_cost", label: "Labor / payroll", hint: "Wages, salaries, contractors" },
  { k: "fixed_expenses", label: "Fixed expenses", hint: "Rent, insurance, subscriptions" },
  { k: "variable_expenses", label: "Variable expenses", hint: "Marketing, supplies, fees" },
  { k: "other_expenses", label: "Other expenses", hint: "Anything else" },
  { k: "sales_target", label: "Sales target", hint: "Your goal for the period" },
  { k: "avg_transaction_value", label: "Avg. sale / ticket", hint: "Optional — for break-even in units" },
];

function Metric({ label, value, sub, status, strong }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
        {status && <Badge status={status} />}
      </div>
      <p className={`mt-1 ${strong ? "text-2xl font-bold" : "text-lg font-semibold"} text-slate-900`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function FinancialAnalysis() {
  const { id } = useParams(); // scan id
  const nav = useNavigate();
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);       // {business_key, business_name}
  const [inputs, setInputs] = useState(null);  // owner inputs form state
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) { nav(`/login?redirect=/scan/${id}/financials`); return; }
    (async () => {
      try {
        const { data: meta } = await api.get(`/scan/${id}/business-key`);
        setCtx(meta);
        const { data: fin } = await api.get(`/financials`, { params: { business_key: meta.business_key } });
        const base = {
          period: "monthly", currency: "USD", currency_symbol: "$", labor_is_fixed: true, notes: "",
          revenue: "", cogs: "", labor_cost: "", fixed_expenses: "", variable_expenses: "",
          other_expenses: "", sales_target: "", avg_transaction_value: "",
        };
        if (fin.exists && fin.inputs) {
          const inp = fin.inputs;
          setInputs({ ...base, ...Object.fromEntries(Object.entries(inp).map(([k, v]) => [k, v == null ? "" : v])) });
          setAnalysis(fin.analysis);
        } else {
          setInputs(base);
        }
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [id, user]);

  const set = (k, v) => setInputs((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!ctx) return;
    setSaving(true);
    try {
      const body = {
        business_key: ctx.business_key, scan_id: id, business_name: ctx.business_name,
        period: inputs.period, currency: inputs.currency, currency_symbol: inputs.currency_symbol,
        labor_is_fixed: inputs.labor_is_fixed, notes: inputs.notes || null,
      };
      FIELDS.forEach(({ k }) => { body[k] = inputs[k] === "" || inputs[k] == null ? null : Number(inputs[k]); });
      const { data } = await api.put(`/financials`, body);
      setAnalysis(data.analysis);
      toast.success("Saved — this analysis is private to you.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clearAll = async () => {
    if (!ctx) return;
    try {
      await api.delete(`/financials`, { params: { business_key: ctx.business_key } });
      setAnalysis(null);
      setInputs((f) => ({ ...f, revenue: "", cogs: "", labor_cost: "", fixed_expenses: "", variable_expenses: "", other_expenses: "", sales_target: "", avg_transaction_value: "", notes: "" }));
      toast.success("Your financial data was cleared.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading || !inputs) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const sym = inputs.currency_symbol || "$";
  const cur = inputs.currency || "USD";
  const a = analysis;
  const prof = a?.profitability, be = a?.break_even, tgt = a?.target;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10" data-testid="financial-analysis-page">
      <Link to={`/report/${id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4" /> Back to report</Link>
      <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em]">Private · Owner only</p>
      <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Financial analysis{ctx?.business_name ? ` — ${ctx.business_name}` : ""}</h1>

      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-2" data-testid="financials-privacy-note">
        <Lock className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
        <p className="text-sm text-slate-600">Everything here is <b>private to your account</b>. These figures are never shown on the public report or on any proposal/shared link. This is a planning aid — not accounting, tax or investment advice.</p>
      </div>

      {/* Inputs */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="financials-inputs">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2"><Calculator className="w-5 h-5 text-indigo-600" /> Your numbers</h2>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Period</Label>
            <Select value={inputs.period} onValueChange={(v) => set("period", v)}>
              <SelectTrigger className="w-32 h-9" data-testid="fin-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(({ k, label, hint }) => (
            <div key={k}>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">{label}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{sym}</span>
                <Input type="number" min="0" inputMode="decimal" className="pl-7" data-testid={`fin-${k}`}
                  value={inputs[k]} onChange={(e) => set(k, e.target.value)} placeholder="0" />
              </div>
              <p className="text-[11px] text-slate-400 mt-1">{hint}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 p-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">Treat labor as a fixed cost</p>
            <p className="text-xs text-slate-500">Affects the break-even calculation. Turn off if your labor scales directly with sales.</p>
          </div>
          <Switch checked={inputs.labor_is_fixed} onCheckedChange={(v) => set("labor_is_fixed", v)} data-testid="fin-labor-fixed" />
        </div>
        <div className="mt-4">
          <Label className="text-sm font-medium text-slate-700 mb-1 block">Notes (private)</Label>
          <Textarea rows={2} value={inputs.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="fin-notes" placeholder="Context for yourself — optional" />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button onClick={save} disabled={saving} data-testid="fin-save" className="bg-slate-900 hover:bg-slate-800">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Calculate &amp; save
          </Button>
          {a?.has_inputs && (
            <Button variant="outline" onClick={clearAll} data-testid="fin-clear" className="text-slate-600 border-slate-200 hover:bg-slate-50">
              <Trash2 className="w-4 h-4 mr-1.5" /> Clear my data
            </Button>
          )}
        </div>
      </div>

      {/* Results */}
      {a && a.has_inputs && (
        <div className="mt-8 space-y-6" data-testid="financials-results">
          {prof?.available && (
            <section>
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Profitability ({a.period_label})</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Metric label="Gross profit" value={fmtMoney(prof.gross_profit, cur)} sub={`${prof.gross_margin_pct}% gross margin`} status="CALCULATED" strong />
                <Metric label="Net profit" value={fmtMoney(prof.net_profit, cur)} sub={`${prof.net_margin_pct}% net margin`} status="CALCULATED" strong />
                <Metric label="Total costs" value={fmtMoney(prof.total_costs, cur)} status="CALCULATED" />
                <Metric label="Operating expenses" value={fmtMoney(prof.operating_expenses, cur)} status="CALCULATED" />
              </div>
              {prof.calc && (
                <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                  <p data-testid="fin-gp-calc">{prof.calc.gross_profit}</p>
                  <p>{prof.calc.net_profit}</p>
                </div>
              )}
            </section>
          )}

          {be && (
            <section>
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-indigo-600" /> Break-even</h3>
              {be.available ? (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Metric label={`Break-even ${a.period}`} value={fmtMoney(be.break_even_revenue, cur)} status="CALCULATED" strong />
                    {be.break_even_units != null && <Metric label="Break-even in sales" value={`${be.break_even_units.toLocaleString()} sales`} status="CALCULATED" />}
                    <Metric label="Contribution margin" value={`${be.contribution_margin_ratio_pct}%`} status="CALCULATED" />
                    {be.margin_of_safety_pct != null && <Metric label="Margin of safety" value={`${be.margin_of_safety_pct}%`} sub={be.above_break_even ? "above break-even" : "below break-even"} status="CALCULATED" />}
                  </div>
                  <p className="mt-2 text-xs text-slate-500" data-testid="fin-be-calc">{be.calc}</p>
                </>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600" data-testid="fin-be-note">{be.note}</div>
              )}
            </section>
          )}

          {tgt?.available && (
            <section>
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><Target className="w-5 h-5 text-indigo-600" /> Sales vs. target</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Metric label="Target" value={fmtMoney(tgt.target, cur)} status="OWNER_PROVIDED" />
                <Metric label="Actual" value={fmtMoney(tgt.actual, cur)} status="OWNER_PROVIDED" />
                <Metric label="% to target" value={`${tgt.pct_to_target}%`} sub={tgt.band === "ahead" ? "Ahead of target" : tgt.band === "on_track" ? "On track" : "Behind target"} status="CALCULATED" strong />
                <Metric label={tgt.met ? "Over target" : "Gap to target"} value={fmtMoney(tgt.met ? tgt.variance : tgt.gap_to_target, cur)} status="CALCULATED" />
              </div>
            </section>
          )}

          {(a.scenarios || []).length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-heading text-lg font-semibold text-slate-900">What-if scenarios</h3>
                <Badge status="MODELED" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3" data-testid="fin-scenarios">
                {a.scenarios.map((s, i) => (
                  <div key={i} className="rounded-xl border border-violet-200 bg-violet-50/50 p-4">
                    <p className="font-semibold text-slate-900 text-sm">{s.label}</p>
                    <p className="mt-1 text-lg font-bold text-slate-900">{fmtMoney(s.projected_net_profit, cur)} <span className="text-xs font-medium text-slate-500">net</span></p>
                    <p className="text-xs text-emerald-700 font-medium">{s.delta_net_profit >= 0 ? "+" : ""}{fmtMoney(s.delta_net_profit, cur)} vs now</p>
                    <p className="text-[11px] text-slate-500 mt-1">{s.note}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Provenance + assumptions */}
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-2"><Info className="w-4 h-4 text-slate-400" /> How to read this</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.keys(PROV).map((k) => <span key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-600"><Badge status={k} /> {PROV[k] && (a.provenance_legend?.[k] || "")}</span>)}
            </div>
            <ul className="list-disc list-inside text-xs text-slate-500 space-y-0.5">
              {(a.assumptions || []).map((x, i) => <li key={i}>{x}</li>)}
            </ul>
            <p className="text-[11px] text-slate-400 mt-2">{a.disclaimer}</p>
          </section>
        </div>
      )}
    </div>
  );
}
